/**
 * AutonomousProgress — visual indicator for multi-round autonomous execution
 *
 * Shows when AI enters autonomous mode (ReAct loop):
 *   - Goal description
 *   - Round progress (1/3, 2/3…)
 *   - Evaluation results between rounds
 *   - Completion or escalation state
 */

import { cn } from '@/lib/utils';
import { Loader2, Target, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import type { AutonomousState } from '@/hooks/useChatStream';

interface AutonomousProgressProps {
  state: AutonomousState;
  streaming?: boolean;
}

export function AutonomousProgress({ state, streaming }: AutonomousProgressProps) {
  const { active, goalDescription, maxRounds = 3, currentRound = 0, evaluations, completed, error } = state;

  // Determine overall status
  const isRunning = active && streaming;
  const isDone = !active && completed;
  const isFailed = !active && !!error;
  const isEscalated = evaluations?.some(e => e.suggestedAction === 'escalate');

  return (
    <div className={cn(
      'rounded-lg border px-3 py-2 mb-2 text-[11px]',
      isRunning && 'border-blue-500/30 bg-blue-500/5',
      isDone && 'border-green-500/30 bg-green-500/5',
      isFailed && 'border-red-500/30 bg-red-500/5',
      isEscalated && 'border-amber-500/30 bg-amber-500/5',
    )}>
      {/* Header */}
      <div className="flex items-center gap-1.5 font-medium text-foreground">
        {isRunning ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500 shrink-0" />
        ) : isDone ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
        ) : isFailed ? (
          <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
        ) : isEscalated ? (
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
        ) : (
          <Target className="h-3.5 w-3.5 text-blue-500 shrink-0" />
        )}
        <span>
          {isRunning ? '自主执行中' : isDone ? '自主执行完成' : isFailed ? '执行出错' : isEscalated ? '需要人工介入' : '自主执行'}
        </span>
        {currentRound > 0 && (
          <span className="text-muted-foreground font-normal ml-auto">
            Round {currentRound}/{maxRounds}
          </span>
        )}
      </div>

      {/* Goal */}
      {goalDescription && (
        <div className="mt-1 text-muted-foreground truncate" title={goalDescription}>
          目标: {goalDescription}
        </div>
      )}

      {/* Round progress bar */}
      {maxRounds > 1 && currentRound > 0 && (
        <div className="mt-1.5 flex gap-1">
          {Array.from({ length: maxRounds }, (_, i) => {
            const roundNum = i + 1;
            const eval_ = evaluations?.find(e => e.round === roundNum);
            const isCurrent = roundNum === currentRound && isRunning;
            const isPast = roundNum < currentRound || (roundNum === currentRound && !isRunning);
            return (
              <div
                key={roundNum}
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  isCurrent && 'bg-blue-500 animate-pulse',
                  isPast && eval_?.isComplete && 'bg-green-500',
                  isPast && !eval_?.isComplete && 'bg-amber-500',
                  !isCurrent && !isPast && 'bg-muted',
                )}
              />
            );
          })}
        </div>
      )}

      {/* Evaluations */}
      {evaluations && evaluations.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {evaluations.map((ev) => (
            <div key={ev.round} className="flex items-start gap-1.5 text-muted-foreground">
              {ev.isComplete ? (
                <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0 mt-0.5" />
              ) : ev.suggestedAction === 'escalate' ? (
                <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" />
              ) : (
                <Target className="h-3 w-3 text-blue-400 shrink-0 mt-0.5" />
              )}
              <span className="line-clamp-2">
                R{ev.round}: {ev.reflection}
                {ev.confidence > 0 && (
                  <span className="ml-1 text-[10px] opacity-70">
                    ({Math.round(ev.confidence * 100)}%)
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-1 text-red-500">
          {error}
        </div>
      )}
    </div>
  );
}
